import { useEffect } from 'react'
import useStore from 'stores/utils/hooks/useStore'
import { resolveTieredVoucherAmount } from '../helpers/voucherAmount'
import type { VoucherTopBannerModel } from '../VoucherBanner.types'
import { trackVoucherAmountTier } from '../helpers/trackVoucherActivation'

const trackedBannerIdentities = new WeakMap<object, Set<string>>()

interface UseVoucherBannerTrackingInput {
  model: VoucherTopBannerModel
  flow: 'standard' | 'rce'
  sourceAmount: number
  decisionKey: string
}

const useVoucherBannerTracking = ({
  model,
  flow,
  sourceAmount,
  decisionKey
}: UseVoucherBannerTrackingInput) => {
  const rootStore = useStore()
  const {
    traversal: { data, traversalId }
  } = useStore()

  useEffect(() => {
    if (model.kind === 'hidden') return

    // Use the tiered mapping as the canonical tracking split: 20k+ -> 100, 50k+ -> 150.
    // This keeps tracking buckets tenant-agnostic and independent of each
    // tenant/experiment's actual voucher eligibility rules.
    const trackedVoucherAmount = resolveTieredVoucherAmount(sourceAmount)

    if (trackedVoucherAmount === null) return

    const amountTier = String(trackedVoucherAmount)
    const identity = [traversalId, flow, amountTier, decisionKey].join(':')
    const identities = trackedBannerIdentities.get(rootStore) || new Set()

    if (identities.has(identity)) return

    identities.add(identity)
    trackedBannerIdentities.set(rootStore, identities)
    const trackingTenant =
      model.kind === 'standard-contact-prompt' ||
      model.kind === 'standard-activated'
        ? model.variant
        : 'rce'

    trackVoucherAmountTier(data, sourceAmount, trackingTenant)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data,
    decisionKey,
    flow,
    model.kind,
    rootStore,
    sourceAmount,
    traversalId
  ])
}

export default useVoucherBannerTracking
